import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, Lock, RefreshCw, Undo2 } from 'lucide-react';
import type { ClosingStep, YearEndPreview } from '@polatkan/api/services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { cn, formatDate } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { Tag } from '@/components/accounting/posting-rules/status-badge';
import { Money, Stat } from '@/components/accounting/tie-out/money';
import { entryHref, type TieOutApp } from '@/components/accounting/tie-out/model';

/** The fiscal year an accountant most likely wants to close today. */
export function defaultClosingYear(today: Date): number {
  // Until April, last year's books are still the ones being closed.
  return today.getUTCMonth() < 3 ? today.getUTCFullYear() - 1 : today.getUTCFullYear();
}

export function stepTone(step: Pick<ClosingStep, 'status'>): 'emerald' | 'amber' | 'neutral' {
  return step.status === 'POSTED' ? 'emerald' : step.status === 'PENDING' ? 'amber' : 'neutral';
}

interface YearEndCloseTabProps {
  app: TieOutApp;
}

export function YearEndCloseTab({ app }: YearEndCloseTabProps) {
  const { t } = useTranslation('periodEnd');
  const today = useMemo(() => new Date(), []);
  const [yearDraft, setYearDraft] = useState(String(defaultClosingYear(today)));
  const [taxDraft, setTaxDraft] = useState('0');
  const [includeOpening, setIncludeOpening] = useState(true);
  const [params, setParams] = useState({ year: defaultClosingYear(today), taxProvision: 0, includeOpening: true });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [voidReason, setVoidReason] = useState('');
  const [confirmVoid, setConfirmVoid] = useState(false);

  const canEdit = useAppStore((s) => s.hasPermissionForApp(app, 'ACCOUNTING', 'EDIT'));
  const utils = trpc.useUtils();
  const input = useMemo(() => ({ app, ...params }), [app, params]);
  const preview = trpc.periodEnd.closePreview.useQuery(input);
  const invalidate = () => utils.periodEnd.closePreview.invalidate(input);
  const post = trpc.periodEnd.closePost.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('close.toast.posted', { count: r.posted.length, net: r.totals.netResult }));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });
  const voidMutation = trpc.periodEnd.closeVoid.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('close.toast.voided', { count: r.voided }));
      setConfirmVoid(false);
      setVoidReason('');
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const data: YearEndPreview | undefined = preview.data;
  const busy = post.isPending || voidMutation.isPending;
  const yearValid = /^\d{4}$/.test(yearDraft);
  const taxValid = /^\d+([.,]\d{1,2})?$/.test(taxDraft.trim());

  return (
    <div className="space-y-6">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (yearValid && taxValid) setParams({ year: Number(yearDraft), taxProvision: Number(taxDraft.replace(',', '.')), includeOpening });
        }}
      >
        <label className="text-xs text-muted-foreground">
          {t('close.year')}
          <Input value={yearDraft} onChange={(e) => setYearDraft(e.target.value)} className="mt-1 h-8 w-[110px] font-mono" inputMode="numeric" />
        </label>
        <label className="text-xs text-muted-foreground">
          {t('close.taxProvision')}
          <Input value={taxDraft} onChange={(e) => setTaxDraft(e.target.value)} className="mt-1 h-8 w-[160px] font-mono" inputMode="decimal" />
        </label>
        <label className="flex h-8 items-center gap-2 text-xs">
          <input type="checkbox" checked={includeOpening} onChange={(e) => setIncludeOpening(e.target.checked)} />
          {t('close.includeOpening')}
        </label>
        <Button type="submit" variant="outline" className="h-8" disabled={!yearValid || !taxValid || preview.isFetching}>
          {preview.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {t('close.apply')}
        </Button>
      </form>

      {preview.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {preview.error && <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{preview.error.message}</div>}

      {data && (
        <>
          {data.problems.length > 0 && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                {t('close.problemsTitle')}
              </div>
              <ul className="mt-1 list-disc pl-5">
                {data.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs">{t('close.problemsHint')}</p>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t('close.totals.income')} value={<Money value={data.totals.income} />} />
            <Stat label={t('close.totals.expenses')} value={<Money value={data.totals.expenses} />} />
            <Stat label={t('close.totals.costReflected')} value={<Money value={data.totals.costReflected} />} />
            <Stat label={t('close.totals.profitBeforeTax')} value={<Money value={data.totals.profitBeforeTax} />} tone={data.totals.profitBeforeTax >= 0 ? 'emerald' : 'red'} />
            <Stat label={t('close.totals.taxProvision')} value={<Money value={data.totals.taxProvision} />} />
            <Stat label={t('close.totals.netResult')} value={<Money value={data.totals.netResult} />} tone={data.totals.netResult >= 0 ? 'emerald' : 'red'} />
          </div>

          <section className="rounded-md border border-border">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div>
                <h2 className="flex items-center gap-2 text-base font-semibold">
                  {data.closed && <Lock className="h-4 w-4 text-emerald-600" />}
                  {t('close.stepsTitle', { year: data.year })}
                </h2>
                <p className="text-xs text-muted-foreground">{t('close.stepsDesc')}</p>
              </div>
              {canEdit ? (
                <div className="flex flex-wrap items-center gap-2">
                  {data.canPost && (
                    <Button size="sm" className="h-8" disabled={busy || data.problems.length > 0} onClick={() => post.mutate(input)}>
                      {post.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                      {post.isPending ? t('close.posting') : t('close.post', { count: data.steps.filter((s) => s.status === 'PENDING').length })}
                    </Button>
                  )}
                  {data.closed &&
                    (confirmVoid ? (
                      <>
                        <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder={t('close.voidReason')} className="h-8 w-[220px]" disabled={busy} />
                        <Button size="sm" variant="destructive" className="h-8" disabled={voidReason.trim().length < 3 || busy} onClick={() => voidMutation.mutate({ app, year: data.year, reason: voidReason.trim() })}>
                          {voidMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
                          {t('close.voidConfirm')}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" disabled={busy} onClick={() => setConfirmVoid(false)}>
                          {t('close.voidCancel')}
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setConfirmVoid(true)}>
                        <Undo2 className="mr-2 h-4 w-4" />
                        {t('close.void')}
                      </Button>
                    ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">{t('close.readOnly')}</span>
              )}
            </header>

            <ol className="divide-y divide-border">
              {data.steps.map((step, index) => {
                const expanded = open[step.key] ?? step.status === 'PENDING';
                const debit = step.lines.reduce((s, l) => s + l.debit, 0);
                return (
                  <li key={step.key}>
                    <button
                      type="button"
                      className="flex w-full flex-wrap items-center gap-3 px-4 py-2 text-left text-sm hover:bg-muted/40"
                      onClick={() => setOpen((o) => ({ ...o, [step.key]: !expanded }))}
                    >
                      {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                      <span className="w-5 text-xs text-muted-foreground">{index + 1}.</span>
                      <span className="font-medium">{t(`close.step.${step.key}`)}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(step.date)}</span>
                      <Tag
                        className={cn(
                          stepTone(step) === 'emerald' && 'text-emerald-700 dark:text-emerald-300',
                          stepTone(step) === 'amber' && 'text-amber-700 dark:text-amber-300'
                        )}
                      >
                        {t(`close.status.${step.status}`)}
                      </Tag>
                      {step.entry && (
                        <Link to={entryHref(app, step.entry.id)} className="font-mono text-xs hover:underline" onClick={(e) => e.stopPropagation()}>
                          {step.entry.entryNumber}
                        </Link>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {step.lines.length > 0 && (
                          <>
                            {t('close.lineCount', { count: step.lines.length })} · <Money value={debit} />
                          </>
                        )}
                      </span>
                    </button>
                    {expanded && step.lines.length > 0 && (
                      <div className="overflow-x-auto border-t border-border bg-muted/20">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                              <th className="px-3 py-1.5 font-medium">{t('close.columns.account')}</th>
                              <th className="px-3 py-1.5 text-right font-medium">{t('close.columns.debit')}</th>
                              <th className="px-3 py-1.5 text-right font-medium">{t('close.columns.credit')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {step.lines.map((l, i) => (
                              <tr key={`${l.accountId}-${i}`} className="border-t border-border/60">
                                <td className="px-3 py-1">
                                  <span className="font-mono text-xs">{l.code}</span> <span className="text-muted-foreground">{l.name}</span>
                                </td>
                                <td className="px-3 py-1 text-right">{l.debit > 0 ? <Money value={l.debit} /> : ''}</td>
                                <td className="px-3 py-1 text-right">{l.credit > 0 ? <Money value={l.credit} /> : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{t('close.afterHint')}</p>
          </section>
        </>
      )}
    </div>
  );
}
